// Impact panel — "what got FIXED, what's still bleeding" (the recurring-
// pattern question). Two compact lists + the API's computed headlines; text,
// not charts, so the four-graphics budget stays intact. Every row deep-links
// to the exact job + signal, because a list you can't act on is decoration.
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { signedPts } from '../../lib/format';

function signalHref({ job_id: jobId, cluster_id: clusterId }) {
  const q = new URLSearchParams();
  if (jobId) q.set('job', jobId);
  if (clusterId) q.set('cluster', clusterId);
  return `/?${q.toString()}`;
}

function ResolvedRow({ row }) {
  const hasDelta = row.delta_pts !== null && row.delta_pts !== undefined;
  return (
    <li>
      <a className="impact__link" href={signalHref(row)} title={`open ${row.cluster_id} in the job view`}>
        <code>{row.title}</code>
      </a>
      <span className="muted">
        {' '}· {row.job_id} · seen in {row.runs_seen} run{row.runs_seen === 1 ? '' : 's'}, gone since
      </span>
      {hasDelta && (
        <span className={`tile__delta${row.delta_pts >= 0 ? ' is-good' : ' is-bad'}`}>
          {' '}{signedPts(row.delta_pts)}
        </span>
      )}
    </li>
  );
}

function ChronicRow({ row }) {
  return (
    <li>
      <a className="impact__link" href={signalHref(row)} title={`open ${row.cluster_id} in the job view`}>
        <code>{row.title}</code>
      </a>
      <span className="muted">
        {' '}· {row.job_id} · {row.runs_seen} runs
        {row.active_days !== null ? ` over ${row.active_days}d` : ''}
        {row.tests_blocked ? ` · blocks ${row.tests_blocked} tests` : ''}
      </span>
    </li>
  );
}

export default function ImpactPanel({ weeks, jobId }) {
  const [impact, setImpact] = useState(null);
  const [error, setError] = useState(null);
  const fetchSeq = useRef(0);

  useEffect(() => {
    const seq = ++fetchSeq.current;
    // Scoped to the SELECTED job (Aug 11 review: the card was global and
    // identical under every filter) — deterministic server-side filter, no AI.
    api.impact({ weeks, jobId })
      .then((d) => { if (seq === fetchSeq.current) setImpact(d); })
      .catch((e) => { if (seq === fetchSeq.current) setError(e.message); });
  }, [weeks, jobId]);

  // Degrade quietly: the four graphics above are the screen's core; a missing
  // impact read must not add an error banner to a healthy page.
  if (error) return null;
  if (!impact) return null;

  const resolved = impact.before_after.top_resolved;
  const chronic = impact.recurrence.chronic_active;
  if (!resolved.length && !chronic.length && !impact.headlines.length) return null;

  return (
    <section className="card impact">
      <header className="card__head">
        <div>
          <h2 className="card__title">Fixed vs still recurring{jobId ? ` — ${jobId}` : ''}</h2>
          <p className="card__subtitle">
            resolved = absent from the job's last 2+ runs · pass-rate move is the job's
            weighted rate during vs after the signal · click a row to open it
          </p>
        </div>
      </header>

      {impact.headlines.length > 0 && (
        <ul className="impact__headlines">
          {impact.headlines.map((h) => <li key={h}>{h}</li>)}
        </ul>
      )}

      <div className="grid grid--even">
        <div>
          <h3 className="detail__h3">Recently resolved ({impact.signals.resolved})</h3>
          {resolved.length
            ? <ul className="impact__list">{resolved.map((r) => <ResolvedRow key={r.cluster_id} row={r} />)}</ul>
            : <p className="muted">Nothing resolved in this window yet — signals disappear here once their job runs clean twice.</p>}
        </div>
        <div>
          <h3 className="detail__h3">Chronic — recurring &amp; still active ({chronic.length})</h3>
          {chronic.length
            ? <ul className="impact__list">{chronic.map((r) => <ChronicRow key={r.cluster_id} row={r} />)}</ul>
            : <p className="muted">No signal has recurred 6+ runs and survived — the healing loop is keeping up.</p>}
        </div>
      </div>
    </section>
  );
}
